import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import api from '../services/api';

// Status color mapping for hearings
const STATUS_COLORS = {
  'Abandoned': '#46c37b',
  'Accepted & Advertised': '#70b9eb',
  'Advertised bef acc': '#5c90d2',
  'Exam Report Issued': '#f3b760',
  'Cancelled': '#d26a5c',
  'Formalities Chk Fail': '#d26a5c',
  'Formalities Chk Pass': '#46c37b',
  'Marked for Exam': '#f3b760',
  'New Application': '#5c90d2',
  'Objected': '#d26a5c',
  'Opposed': '#d26a5c',
  'Refused': '#f3b760',
  'Registered': '#70b9eb',
  'Removed': '#d26a5c',
  'Send Back to EDP': '#d26a5c',
  'Send to PRAS': '#46c37b',
  'Send To Vienna Codification': '#d26a5c',
  'Withdrawn': '#5c90d2',
  'Withdraw Found': '#5c90d2',
  'Rectification Filed': '#f3b760',
  'Accepted/Readvertise': '#70b9eb',
  'Accepted': '#46c37b',
  'ABA/Readvertised': '#70b9eb',
  'Protection Granted': '#46c37b',
  'IR Cancelled': '#d26a5c',
  'Deemed to be Abandoned': '#46c37b',
  'Online New Appl.': '#5c90d2',
  'INVALID (No fee received)': '#d26a5c',
  'Protection Refused': '#f3b760',
  'Provisional Refusal': '#f3b760',
  'Under Division': '#5c90d2',
  'Send to Re-Advertised': '#70b9eb',
  'Acceptance Withdrawn Process': '#d26a5c',
  'Opposition Withdrawn': '#46c37b'
};

const Hearings = () => {
  const navigate = useNavigate();
  const [hearingsData, setHearingsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [testing, setTesting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    appno: '',
    status: [],
    tmAppliedFor: '',
    buisnessName: '',
    registryLocation: '',
    type: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'dsc'

  // Helper function to render colored status badge
  const renderStatusBadge = (status) => {
    if (!status) return '-';
    const color = STATUS_COLORS[status] || '#6b7280'; // Default gray if status not found
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color: color }}
      >
        {status}
      </span>
    );
  };

  // Helper function to render colored type badge
  const renderTypeBadge = (type) => {
    if (!type) return '-';
    // Use a different color scheme for types
    const typeColors = {
      'Inbound': '#3b82f6', // Blue
      'Outbound': '#10b981', // Green
      'Internal': '#f59e0b', // Amber
      'External': '#8b5cf6', // Purple
      'Hearing': '#ef4444', // Red
      'Examination': '#f97316', // Orange
      'Opposition': '#dc2626', // Dark red
      'Renewal': '#059669', // Dark green
      'Assignment': '#7c3aed', // Dark purple
      'Licensing': '#d97706' // Dark amber
    };
    const color = typeColors[type] || '#6b7280'; // Default gray if type not found
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: `${color}15`, color: color }}
      >
        {type}
      </span>
    );
  };

  // Status options for multi-select
  const STATUS_OPTIONS = [
    'Registered',
    'Opposed',
    'Opposed*',
    'Marked for Exam',
    'New Application',
    'Withdrawn',
    'Objected',
    'Abandoned',
    'Refused',
    'Accepted & Advertised',
    'Exam Report Issued',
    'Rectification Filed',
    'Accepted/Readvertise',
    'Advertised bef acc',
    'Formalities Chk Fail',
    'Formalities Chk Pass',
    'Send To Vienna Codification',
    'Accepted',
    'Removed',
    'Send Back to EDP',
    'Cancelled',
    'ABA/Readvertised',
    'Send to PRAS',
    'Protection Granted',
    'IR Cancelled',
    'Deemed to be Abandoned',
    'Online New Appl.',
    'INVALID (No fee received)',
    'Protection Refused',
    'Provisional Refusal',
    'Under Division',
    'Send to Re-Advertised',
    'Acceptance Withdrawn Process',
    'Opposition Withdrawn'
  ];

  const fetchHearingsData = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching hearings data with pagination and filters...');

      // Build URL with filters
      let url = `https://agent3.tmpilot.com/api/global/hearings?offset=${pageOffset}&limit=${limit}&sortBy=daysLeftToHearing&dir=${sortDirection}`;

      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          // Handle array fields (like status)
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          // Handle string fields
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });

      console.log('🔗 Request URL:', url);

      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Hearings data received:', response.data.data);
        const data = response.data.data;

        if (data.response.data && Array.isArray(data.response.data)) {
          setHearingsData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} hearings records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load hearings data');
      }
    } catch (error) {
      console.error('Error fetching hearings data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load hearings data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Separate function for fetching data with filters
  const fetchHearingsDataWithFilters = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching hearings data with pagination and filters...');

      // Build URL with filters
      let url = `https://agent3.tmpilot.com/api/global/hearings?offset=${pageOffset}&limit=${limit}&sortBy=daysLeftToHearing&dir=${sortDirection}`;

      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          // Handle array fields (like status)
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          // Handle string fields
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });

      console.log('🔗 Request URL:', url);

      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Hearings data received:', response.data.data);
        const data = response.data.data;

        if (data.response.data && Array.isArray(data.response.data)) {
          setHearingsData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} hearings records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load hearings data');
      }
    } catch (error) {
      console.error('Error fetching hearings data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load hearings data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, filters, sortDirection]);

  const testApiConnection = async () => {
    setTesting(true);
    try {
      console.log('🧪 Testing API connection...');
      const response = await api.post('/api-data/get-data', {
        url: 'https://agent3.tmpilot.com/api/global/hearings?offset=0&limit=1'
      });

      if (response.data.success) {
        toast.success('API connection test successful!');
        console.log('✅ API test successful:', response.data.data);
      } else {
        toast.error(response.data.message || 'API connection test failed');
      }
    } catch (error) {
      console.error('API test error:', error);
      toast.error(error.response?.data?.message || error.message || 'API connection test failed');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchHearingsData();
  }, [fetchHearingsData]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * limit;
    setCurrentPage(newPage);
    setOffset(newOffset);

    // Use appropriate fetch function based on whether filters are applied
    if (filtersApplied) {
      fetchHearingsDataWithFilters(newOffset);
    } else {
      fetchHearingsData(newOffset);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle status multi-select changes
  const handleStatusChange = (statusValue, checked) => {
    setFilters(prev => ({
      ...prev,
      status: checked
        ? [...prev.status, statusValue]
        : prev.status.filter(s => s !== statusValue)
    }));
  };

  // Handle sort direction changes
  const handleSortDirectionChange = (direction) => {
    setSortDirection(direction);
    setCurrentPage(1);
    setOffset(0);

    // Make direct API call with new sort direction
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('📡 Fetching hearings data with new sort direction...');

        // Build URL with current filters and new sort direction
        let url = `https://agent3.tmpilot.com/api/global/hearings?offset=0&limit=${limit}&sortBy=daysLeftToHearing&dir=${direction}`;

        // Add filters to URL if they have values
        Object.keys(filters).forEach(key => {
          if (Array.isArray(filters[key]) && filters[key].length > 0) {
            // Handle array fields (like status)
            filters[key].forEach(value => {
              url += `&filters[${key}][]=${encodeURIComponent(value)}`;
            });
          } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
            // Handle string fields
            url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
          }
        });

        console.log('🔗 Request URL:', url);

        const response = await api.post('/api-data/get-data', {
          url: url
        });

        if (response.data.success) {
          console.log('✅ Hearings data received:', response.data.data);
          const data = response.data.data;

          if (data.response.data && Array.isArray(data.response.data)) {
            setHearingsData(data.response.data);
            setTotalCount(data.response.total || 0);
            setLastFetched(new Date());
            toast.success(`Loaded ${data.response.data.length} hearings records`);
          } else {
            throw new Error('Invalid data format received from API');
          }
        } else {
          throw new Error(response.data.message || 'Failed to load hearings data');
        }
      } catch (error) {
        console.error('Error fetching hearings data:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load hearings data';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  // Apply filters and fetch data from server
  const applyFilters = () => {
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(true);
    fetchHearingsDataWithFilters(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      appno: '',
      status: [],
      tmAppliedFor: '',
      buisnessName: '',
      registryLocation: '',
      type: ''
    });
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(false);
    fetchHearingsData(0);
  };

  // Handle application number click
  const handleAppNoClick = (appno) => {
    navigate(`/api-data/trademark-portfolio/${appno}`);
  };

  const totalPages = Math.ceil(totalCount / limit);

  // Format date to show only year
  const formatYear = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).getFullYear();
    } catch {
      return dateString.split('-')[0] || '-';
    }
  };

  // Get status color for styling
  const getStatusColor = (status) => {
    if (!status) return '#6b7280'; // Default gray color
    return STATUS_COLORS[status] || '#6b7280'; // Return mapped color or default gray
  };

  // Format hearing date
  const formatHearingDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages.map((pageNum, index) => {
      if (pageNum === '...') {
        return (
          <span
            key={`ellipsis-${index}`}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
          >
            ...
          </span>
        );
      }

      return (
        <button
          key={pageNum}
          onClick={() => handlePageChange(pageNum)}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === currentPage
              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
            }`}
        >
          {pageNum}
        </button>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Hearings</h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage trademark hearing schedules
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Sort by Days Left:</label>
                <select
                  value={sortDirection}
                  onChange={(e) => handleSortDirectionChange(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              <button
                onClick={() => {
                  if (filtersApplied) {
                    fetchHearingsDataWithFilters(offset);
                  } else {
                    fetchHearingsData();
                  }
                }}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Options</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application No
                  </label>
                  <input
                    type="text"
                    value={filters.appno}
                    onChange={(e) => handleFilterChange('appno', e.target.value)}
                    placeholder="Filter by application no..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="relative">
                    <div
                      className="border border-gray-300 rounded-md p-2 min-h-[42px] bg-white cursor-pointer"
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    >
                      {filters.status.length === 0 ? (
                        <span className="text-gray-500 text-sm">Select statuses...</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {filters.status.map((status, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {status}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(status, false);
                                }}
                                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                              >
                                <span className="sr-only">Remove {status}</span>
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Options Dropdown */}
                  {showStatusDropdown && (
                    <div className="mt-1 max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg z-10">
                      {STATUS_OPTIONS.map((status) => (
                        <label key={status} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.status.includes(status)}
                            onChange={(e) => handleStatusChange(status, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{status}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trademark Applied For
                  </label>
                  <input
                    type="text"
                    value={filters.tmAppliedFor}
                    onChange={(e) => handleFilterChange('tmAppliedFor', e.target.value)}
                    placeholder="Filter by trademark..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={filters.buisnessName}
                    onChange={(e) => handleFilterChange('buisnessName', e.target.value)}
                    placeholder="Filter by business name..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registry Location
                  </label>
                  <input
                    type="text"
                    value={filters.registryLocation}
                    onChange={(e) => handleFilterChange('registryLocation', e.target.value)}
                    placeholder="Filter by registry location..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <input
                    type="text"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    placeholder="Filter by type..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Filter Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={clearFilters}
                  className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Clear Filters
                </button>
                <button
                  onClick={applyFilters}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  {loading ? 'Applying...' : 'Apply Filters'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Hearings Records ({totalCount} total)
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Showing page {currentPage} of {totalPages}
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500 hover:bg-blue-400 transition ease-in-out duration-150 cursor-not-allowed">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading hearings data...
              </div>
            </div>
          ) : hearingsData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No hearings found for the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hearing Information
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status & Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {hearingsData.map((hearing) => (
                    <tr key={hearing.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="py-4 whitespace-nowrap text-sm text-gray-900">
                          {hearing.tmAppliedFor || '-'}
                          <br></br> {hearing.buisnessName || '-'}
                        </span>
                        <br></br>
                        <button
                          onClick={() => handleAppNoClick(hearing.appno)}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                        >
                          {hearing.appno}
                        </button>


                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatHearingDate(hearing.hearingDate)}
                        <br></br>
                        <span className="text-gray-500 text-xs">
                          {hearing.daysLeftToHearing !== null ? `${hearing.daysLeftToHearing} days left` : '-'} - {hearing.registryLocation || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {renderStatusBadge(hearing.status)}
                        <br></br>
                        <span className="text-gray-500 text-xs">
                          {renderTypeBadge(hearing.type)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span> pages
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  {renderPagination()}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hearings; 